import { useCallback, useMemo } from "react";
import {
  createBrowserRouter,
  Navigate,
  RouterProvider,
  useSearchParams,
} from "react-router-dom";
import { LoginPage } from "./pages/LoginPage";
import { FlightsPage } from "./pages/FlightsPage";
import { ViewerPage } from "./pages/ViewerPage";
import { FramesPage } from "./pages/FramesPage";
import { OfflinePage } from "./pages/OfflinePage";
import { UsagePage } from "./pages/UsagePage";
import { RequireAuth } from "./auth/RequireAuth";
import { AuthedLayout } from "./components/AuthedLayout";
import { useApiClient } from "./api/ApiClientContext";
import { AnnotationsApi } from "./api/annotations";
import { refreshFlight } from "./offline/refreshFlight";
import { makeFetchManifest } from "./offline/fetchManifest";
import { OfflineQuotaExceededError } from "./offline/download";
import { useAuth } from "./auth/AuthContext";

/**
 * App-level wrapper that wires OfflinePage's onRefresh prop to the live API
 * client. Kept inline (not its own file) because the wiring is one screen of
 * glue and lives or dies with OfflinePage's route binding.
 */
function OfflinePageRoute(): JSX.Element {
  const apiClient = useApiClient();
  const [searchParams] = useSearchParams();
  const teamParam = searchParams.get("team") ?? undefined;
  const { token } = useAuth();
  const workerOrigin = import.meta.env.VITE_API_URL ?? "http://localhost:8787";
  const annotationsApi = useMemo(() => new AnnotationsApi(apiClient), [apiClient]);
  // Authed GET of a Worker manifest URL as text — required to refresh a cached
  // HLS-rung flight (enumerate the master playlist for the chosen rung).
  const fetchManifest = useMemo(
    () => makeFetchManifest(token, workerOrigin), [token, workerOrigin],
  );

  const handleRefresh = useCallback(
    async (flightId: string): Promise<void> => {
      try {
        let upToDate = false;
        await refreshFlight({
          flightId,
          teamParam,
          fetchDetail: (id, team) => apiClient.getFlight(id, { team }),
          fetchPanoramas: (id, team) => apiClient.getPanoramas(id, { team }),
          fetchFramePresign: (id, frameId, variant, team) =>
            annotationsApi.frameUrl(id, frameId, variant, team),
          fetchAnnotations: (id, team) => annotationsApi.get(id, team),
          fetchOverlayList: async (team) => (await apiClient.listOverlays(team)).overlays,
          fetchOverlayBytes: (id, team) => apiClient.getOverlayBytes(id, team),
          authToken: token ?? undefined,
          workerOrigin,
          fetchManifest,
          onUpToDate: () => {
            upToDate = true;
          },
        });
        if (upToDate) {
          window.alert("Already up to date.");
        } else {
          window.alert("Flight refreshed.");
        }
      } catch (err) {
        if (err instanceof OfflineQuotaExceededError) {
          window.alert("Storage full — remove other cached flights from /offline");
        } else {
          window.alert(`Refresh failed: ${(err as Error).message}`);
        }
      }
    },
    [apiClient, annotationsApi, teamParam, token, workerOrigin, fetchManifest],
  );

  return <OfflinePage onRefresh={handleRefresh} />;
}

const router = createBrowserRouter([
  { path: "/login", element: <LoginPage /> },
  {
    path: "/flights",
    element: (
      <RequireAuth>
        <AuthedLayout>
          <FlightsPage />
        </AuthedLayout>
      </RequireAuth>
    ),
  },
  {
    path: "/flights/:id",
    element: (
      <RequireAuth>
        <AuthedLayout>
          <ViewerPage />
        </AuthedLayout>
      </RequireAuth>
    ),
  },
  {
    path: "/flights/:id/frames",
    element: (
      <RequireAuth>
        <AuthedLayout>
          <FramesPage />
        </AuthedLayout>
      </RequireAuth>
    ),
  },
  {
    path: "/offline",
    element: (
      <RequireAuth>
        <AuthedLayout>
          <OfflinePageRoute />
        </AuthedLayout>
      </RequireAuth>
    ),
  },
  {
    path: "/usage",
    element: (
      <RequireAuth>
        <AuthedLayout>
          <UsagePage />
        </AuthedLayout>
      </RequireAuth>
    ),
  },
  { path: "/", element: <Navigate to="/flights" replace /> },
  { path: "*", element: <Navigate to="/flights" replace /> },
]);

export function App(): JSX.Element {
  return <RouterProvider router={router} />;
}
